import { Helmet } from 'react-helmet-async'
import { Navigate, useParams } from 'react-router-dom'
import { clsx } from 'clsx'

import styles from './ModelComparisonPage.module.scss'
import baseStyles from '@/pages/base.module.scss'

import Button from '@/components/Button'
import { useGtAudio } from '@/pages/app/AttributionViewer/annotate/useGtAudio'
import ModelComparison, { useTurnPreferenceQuery } from '@/pages/app/AttributionViewer/components/ModelComparison'
import { viewerHref } from '@/pages/app/AttributionViewer/mode'

// Blind by construction: this page renders nothing but the comparison, so no provider name, lane or job can leak.
const ModelComparisonPage = () => {
  const { meetingId } = useParams<{ meetingId: string }>()
  if (!meetingId) return <Navigate to="/app/attribution" replace />
  return <ComparePage meetingId={meetingId} />
}

const ComparePage = ({ meetingId }: { meetingId: string }) => {
  const query = useTurnPreferenceQuery(meetingId)
  const hasUnits = (query.data?.units?.length ?? 0) > 0
  const audio = useGtAudio(meetingId, hasUnits)

  const audioStatus = (() => {
    switch (audio.state) {
      case 'loading':
        return 'Loading audio…'
      case 'absent':
        return 'No audio is retained for this meeting. Compare by text.'
      case 'error':
        return (
          <>
            {"Couldn't load the audio. Try again."}
            <Button title="Retry" type="light" onClick={audio.retry} />
          </>
        )
      default:
        return null
    }
  })()

  return (
    <div className={clsx(baseStyles.container, styles.page)}>
      <Helmet>
        <title>Model comparison</title>
      </Helmet>
      <div className={styles.content}>
        <header className={styles.header}>
          <Button title="← Back to viewer" type="light" to={viewerHref(meetingId, 'inspect')} />
          <h1>Model comparison</h1>
        </header>
        <audio
          key={`${audio.audioUrl}:${audio.loadAttempt}`}
          ref={audio.audioRef}
          src={audio.audioUrl ?? undefined}
          preload="metadata"
          className={styles.audioElement}
          onLoadedMetadata={audio.onLoadedMetadata}
          onTimeUpdate={audio.onTimeUpdate}
          onSeeked={audio.onSeeked}
          onPlay={audio.onPlay}
          onPause={audio.onPause}
          onEnded={audio.onEnded}
          onError={audio.onError}
        />
        {audioStatus !== null && (
          <p className={styles.audioStatus} role="status">
            {audioStatus}
          </p>
        )}
        <ModelComparison
          meetingId={meetingId}
          query={query}
          playClip={(startMs, endMs) => audio.playFrom(startMs, endMs)}
          pause={audio.pause}
          isPlaying={audio.isPlaying}
          audioTime={audio.audioTime}
          playDisabled={audio.state !== 'present'}
        />
      </div>
    </div>
  )
}

export default ModelComparisonPage
